import React, { useState, useEffect } from "react";
import styled from "styled-components";
import CartItem from "../components/CartItem";
import { useCart } from "../context/CartContext";
import { useLocation, useNavigate } from "react-router-dom";
import BottomMenu from "../components/BottomMenu";

export default function CartScreenVoucher() {
    const { cartItems, setCartItems } = useCart();
    const navigate = useNavigate();
    const location = useLocation();
    const selectedFridgeId = location.state?.selectedFridgeId ?? localStorage.getItem("selectedFridgeId") ?? null;
    const [fridgeName, setFridgeName] = useState("");
    const [showTopupToast, setShowTopupToast] = useState(false);

    useEffect(() => {
        // Show success toast if coming back from a successful voucher top-up
        const flag = localStorage.getItem("voucherTopupSuccess");
        if (flag === "true") {
            setShowTopupToast(true);
            try { localStorage.removeItem("voucherTopupSuccess"); } catch {}
            const t = setTimeout(() => setShowTopupToast(false), 3500);
            return () => clearTimeout(t);
        }
    }, []);

    useEffect(() => {
        const fetchFridgeDetails = async () => {
            if (!selectedFridgeId) return;
            try {
                const response = await fetch(
                    `https://boxmealadminapinew.azurewebsites.net/api/Fridge/${selectedFridgeId}`
                );
                if (!response.ok) {
                    console.error("Failed to fetch fridge details", response.status);
                    return;
                }
                const data = await response.json();
                setFridgeName(data.fridgeName || "Unknown Fridge");
                console.log("🔵 Fetched fridge details:", data);
            } catch (error) {
                console.error("🔴 Error fetching fridge details:", error);
            }
        };
        fetchFridgeDetails();
    }, [selectedFridgeId]);

    const handleRemove = (itemId) => {
        const updatedCartItems = cartItems.filter((item) => item.id !== itemId);
        setCartItems(updatedCartItems);
        console.log("🟢 Item removed from cart. Remaining items:", updatedCartItems);
    };

    const calculateTotal = () => {
        const total = cartItems
            .reduce((acc, item) => acc + Number(item.price) * Number(item.quantity), 0)
            .toFixed(2);
        console.log("🟣 Cart total recalculated:", total);
        return total;
    };

    return (
        <Container>
            {showTopupToast && (
                <Toast role="status" aria-live="polite">Saldo påfyllt</Toast>
            )}

            {cartItems.length > 0 ? (
                <ContentWrapper>
                    <ItemsList>
                        {cartItems.map((item) => (
                            <div key={item.id}>
                                <CartItem
                                    imageURL={item.imageURL}
                                    title={item.title}
                                    price={item.price}
                                    quantity={item.quantity}
                                    onRemove={() => handleRemove(item.id)}
                                />
                            </div>
                        ))}
                    </ItemsList>

                    <CheckoutSection>
                        <FridgeInfo>
                            <FridgeLabel>Du handlar från:</FridgeLabel>
                            <FridgeNameText>{fridgeName}</FridgeNameText>
                        </FridgeInfo>

                        <Divider />

                        <TotalSection>
                            <TotalLabel>Totalt</TotalLabel>
                            <TotalAmount>{calculateTotal()}kr</TotalAmount>
                        </TotalSection>

                        <CheckoutButton
                            onClick={() =>
                                navigate("/choose-payment", { state: { selectedFridgeId, voucherMode: true } })
                            }
                        >
                            Betala med Swish
                        </CheckoutButton>
                    </CheckoutSection>
                </ContentWrapper>
            ) : (
                <EmptyContainer>
                    <EmptyTitle>Din varukorg är tom</EmptyTitle>
                    <EmptyText>Lägg till produkter för att börja handla</EmptyText>
                    <BackToProductsButton onClick={() => navigate(-1)}>
                        Tillbaka
                    </BackToProductsButton>
                </EmptyContainer>
            )}

            <BottomMenuSpacer />
            <BottomMenu selectedFridgeId={selectedFridgeId} />
        </Container>
    );
}

/* ===== Styles cloned from CartScreen ===== */
const Container = styled.div`
    height: 100dvh; /* was 100vh; use dynamic viewport on mobile */
    min-height: 100vh; /* fallback */
    background: #f6f7fa;
    display: flex;
    flex-direction: column;
    overflow-x: hidden; /* avoid vertical clipping; was overflow: hidden */
`;

const ContentWrapper = styled.div`
    flex: 1;
    padding: 20px;
    display: flex;
    flex-direction: column;
    padding-top: 40px;
    overflow: hidden;
`;

const ItemsList = styled.div`
    flex: 1;
    margin-bottom: 200px;
    padding-bottom: 20px;
    padding-top: 12px; /* add breathing room so the first card isn't cut at the top */
    overflow-y: auto;

    /* Smooth scrolling */
    scroll-behavior: smooth;

    /* Custom scrollbar */
    &::-webkit-scrollbar {
        width: 6px;
    }

    &::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 3px;
    }

    &::-webkit-scrollbar-thumb {
        background: #ccc;
        border-radius: 3px;

        &:hover {
            background: #999;
        }
    }

    -webkit-overflow-scrolling: touch; /* iOS momentum scroll */
    overscroll-behavior-y: contain; /* prevent parent/body scroll chaining */
`;

const CheckoutSection = styled.div`
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(16px);
    border-top: 1px solid rgba(255, 255, 255, 0.9);
    border-radius: 25px 25px 0 0;
    padding: 20px 20px 100px 20px;
    box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.1);
    z-index: 999;
`;

const FridgeInfo = styled.div`
    text-align: center;
    margin-bottom: 16px;
`;

const FridgeLabel = styled.div`
    font-size: 14px;
    color: #6b7280;
    margin-bottom: 4px;
`;

const FridgeNameText = styled.div`
    font-size: 16px;
    font-weight: 600;
    color: #1f2937;
`;

const Divider = styled.hr`
    border: none;
    height: 1px;
    background: #e5e7eb;
    margin: 16px 0;
`;

const TotalSection = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    gap: 8px;
`;

const TotalLabel = styled.div`
    font-size: 20px;
    font-weight: 600;
    color: #1f2937;
`;

const TotalAmount = styled.div`
    font-size: 20px;
    font-weight: 700;
    color: #1f2937;
`;

const CheckoutButton = styled.button`
    width: 100%;
    background: rgba(37, 37, 37, 0.9);
    color: white;
    border: none;
    border-radius: 16px;
    padding: 16px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    text-transform: uppercase;
    letter-spacing: 0.5px;

    &:hover {
        background: rgba(37, 37, 37, 1);
        transform: translateY(-1px);
        box-shadow: 0 4px 16px rgba(37, 37, 37, 0.3);
    }

    &:active {
        transform: translateY(0);
    }
`;

const EmptyContainer = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    text-align: center;
`;

const EmptyTitle = styled.h2`
    font-size: 24px;
    font-weight: 600;
    color: #1f2937;
    margin: 0 0 8px 0;
`;

const EmptyText = styled.p`
    font-size: 16px;
    color: #6b7280;
    margin: 0 0 32px 0;
`;

const BackToProductsButton = styled.button`
    background: rgba(37, 37, 37, 0.9);
    color: white;
    border: none;
    border-radius: 16px;
    padding: 14px 28px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
        background: rgba(37, 37, 37, 1);
        transform: translateY(-1px);
    }
`;

const BottomMenuSpacer = styled.div`
    height: 80px;
    flex-shrink: 0;
`;

/* Voucher-specific toast preserved */
const Toast = styled.div`
  position: fixed;
  top: 56px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1100;
  background: #16a34a;
  color: #fff;
  padding: 10px 16px;
  border-radius: 9999px;
  font-weight: 700;
  box-shadow: 0 4px 14px rgba(0,0,0,0.15);
`;
